import React, { useState, useEffect } from 'react';
import { Sparkles, Heart, Info } from 'lucide-react';
import { getBoostSettings, updateBoostSettings, getUndiscoveredCreators } from '../services/api';
import { Button } from './ui/button';
import { toast } from 'sonner';

export function UndiscoveredBoostSettings() {
  const [settings, setSettings] = useState(null);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [settingsRes, creatorsRes] = await Promise.all([
        getBoostSettings(),
        getUndiscoveredCreators(5)
      ]);
      setSettings(settingsRes.data);
      setCreators(creatorsRes.data || []);
    } catch (error) {
      console.error('Error fetching boost data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    const newEnabled = !settings.enable_undiscovered_boost;
    setSettings({ ...settings, enable_undiscovered_boost: newEnabled });
    
    try {
      setSaving(true);
      await updateBoostSettings({
        enable_undiscovered_boost: newEnabled,
        boost_percentage: settings.boost_percentage
      });
      toast.success(newEnabled ? 'Boost enabled! Thank you for supporting new creators.' : 'Boost disabled');
    } catch (error) {
      toast.error('Failed to update settings');
      setSettings({ ...settings, enable_undiscovered_boost: !newEnabled });
    } finally {
      setSaving(false);
    }
  };

  const handlePercentageChange = async (newPercentage) => {
    setSettings({ ...settings, boost_percentage: newPercentage });
    
    try {
      await updateBoostSettings({
        enable_undiscovered_boost: settings.enable_undiscovered_boost,
        boost_percentage: newPercentage
      });
    } catch (error) {
      toast.error('Failed to update percentage');
    }
  };

  if (loading) {
    return (
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 animate-pulse">
        <div className="h-8 w-64 bg-[var(--border)] rounded mb-4" />
        <div className="h-24 bg-[var(--border)] rounded" />
      </div>
    );
  }

  if (!settings) return null;

  const boostAmount = (2.00 * settings.boost_percentage).toFixed(2);

  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl overflow-hidden" data-testid="boost-settings">
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border-b border-[var(--border)]">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
            <Sparkles size={24} className="text-purple-500" />
          </div>
          <div>
            <h2 className="text-xl font-teko">Undiscovered Creator Boost</h2>
            <p className="text-sm text-[var(--text-secondary)]">
              Help new creators get discovered
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Toggle */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="font-medium">Enable Boost</p>
            <p className="text-sm text-[var(--text-secondary)]">
              {settings.enable_undiscovered_boost
                ? `$${boostAmount} of your $2.00 goes to emerging creators`
                : 'All your support goes to creators you read'
              }
            </p>
          </div>
          <button
            onClick={handleToggle}
            disabled={saving}
            className={`w-14 h-8 rounded-full transition-colors flex items-center px-1 ${
              settings.enable_undiscovered_boost ? 'bg-purple-500' : 'bg-gray-500'
            }`}
          >
            <div className={`w-6 h-6 bg-white rounded-full transition-transform ${
              settings.enable_undiscovered_boost ? 'translate-x-6' : 'translate-x-0'
            }`} />
          </button>
        </div>

        {/* Percentage Slider */}
        {settings.enable_undiscovered_boost && (
          <div className="mb-6 p-4 bg-[var(--background)] rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Boost Amount</span>
              <span className="text-lg font-teko text-purple-500">
                {(settings.boost_percentage * 100).toFixed(0)}% (${boostAmount})
              </span>
            </div>
            <input
              type="range"
              min="10"
              max="20"
              value={settings.boost_percentage * 100}
              onChange={(e) => handlePercentageChange(parseInt(e.target.value) / 100)}
              className="w-full accent-purple-500"
            />
            <div className="flex justify-between text-xs text-[var(--text-secondary)] mt-1">
              <span>10% ($0.20)</span>
              <span>20% ($0.40)</span>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg mb-6">
          <div className="flex items-start gap-3">
            <Info size={18} className="text-purple-500 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-purple-400 mb-1">How it works</p>
              <p className="text-[var(--text-secondary)]">
                Your boost amount is pooled with other boosters and distributed to:
              </p>
              <ul className="mt-2 space-y-1 text-[var(--text-secondary)]">
                <li>• New creators (joined in last 30 days)</li>
                <li>• Creators earning less than $50/month</li>
                <li>• Based on quality metrics, not popularity</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Featured Undiscovered Creators */}
        {creators.length > 0 && (
          <div>
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <Heart size={16} className="text-pink-500" />
              Creators You Could Help
            </h3>
            <div className="space-y-3">
              {creators.map(creator => (
                <div key={creator.id} className="flex items-center gap-3 p-3 bg-[var(--background)] rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-[var(--surface)] overflow-hidden flex-shrink-0">
                    {creator.profile_picture ? (
                      <img src={creator.profile_picture} alt={creator.username} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-lg font-bold text-[var(--text-secondary)]">
                        {creator.username[0]}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{creator.username}</p>
                    <p className="text-xs text-[var(--text-secondary)]">
                      {creator.comic_count} comic{creator.comic_count !== 1 ? 's' : ''}
                      {creator.is_new && <span className="ml-2 text-green-500">• New Creator</span>}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[var(--text-secondary)]">Earned</p>
                    <p className="font-medium">${creator.total_earnings?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Impact Note */}
      <div className="p-4 bg-[var(--background)] border-t border-[var(--border)]">
        <p className="text-center text-sm text-[var(--text-secondary)]">
          {settings.enable_undiscovered_boost
            ? "You're helping build a diverse creator ecosystem. Thank you!"
            : "Enable boost to help new voices get discovered"
          }
        </p>
      </div>
    </div>
  );
}

export default UndiscoveredBoostSettings;
